import { uniqueClientId } from '@modules/application/apollo-client';
import dayjs from 'dayjs';
import { getTenantIdentifier } from '../utils/getTenantIdentifier';
import { TypedDocumentNode } from '@apollo/client';
import _ from 'lodash';

export class ApiError extends Error {
  public readonly errorCode: number;
  public readonly traceId?: string;

  constructor(errorCode: number, message?: string, traceId?: string) {
    super(message);
    this.errorCode = errorCode;
    this.traceId = traceId;
  }
}

export class TenantMissingError extends Error {
}

export class DomainError extends Error {
  public readonly errors: unknown[];

  constructor(message?: string, errors?: unknown[]) {
    super(message);
    this.errors = errors ?? [];
  }
}

export const fetchData = <TData, TVariables>(
  query: string,
  variables?: TVariables,
  options?: RequestInit['headers'],
): (() => Promise<TData>) => {
  let tenantId = getTenantIdentifier();

  if (query.includes("startTrial")) {
    tenantId ??= 'ariv';
  }

  return async () => {
    if (!tenantId) {
      throw new TenantMissingError();
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': dayjs.locale(),
      'Unique-Client-Id': uniqueClientId,
      ...(options && typeof options === 'object' && !Array.isArray(options) ? (options as Record<string, string>) : {}),
    };

    const response = await fetch(`/${tenantId}/graphql`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    const result = await response.json();

    const code = result.errors?.[0]?.extensions?.code;

  
    if (code === 'AUTH_NOT_AUTHENTICATED') {
      throw new ApiError(401, result.errors[0].message);
    } else if (code === 'AUTH_NOT_AUTHORIZED') {
      throw new ApiError(403, result.errors[0].message);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new ApiError(response.status, result.errors?.[0]?.message ?? response.statusText);
    }

    const resultQueryNames = Object.keys(result.data);
    for (const index in resultQueryNames) {
      const errors = result.data[resultQueryNames[index]]?.errors;

      if (errors) {
        const error = errors[0];
        throw new DomainError(error.message, errors);
      }
    }

    return result.data;
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getQueryKey = (document: TypedDocumentNode<any, any>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (document.definitions?.[0] as any)?.name.value ?? 'unknown';
};
